import { useTranslation } from "@equiem/web-ng-lib";
import { Site, stringNotEmpty, Theme, Viewer } from "@equiem/web-ng-lib";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useContext, useMemo } from "react";
import { useCallback } from "react";
import { ParentLink } from "./ParentLink";

interface Props {
  className?: string;
}

export const MenuLinks: React.FC<Props> = ({ className = "" }) => {
  const { t } = useTranslation();

  const site = useContext(Site);
  const viewer = useContext(Viewer);
  const { breakpoints } = useContext(Theme);
  const { asPath } = useRouter();
  const nsnx = "?ns=right_nav&nx=1";

  const subject =
    viewer?.profile?.email != null
      ? encodeURIComponent(
          `${t("main.webFeedback")} - ${site.name} - ${viewer.profile.email}`,
        )
      : encodeURIComponent(`${t("main.webFeedback")} - ${site.name}`);
  const mailTo = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      window.open(
        `mailto:${site.email ?? "support@getequiem.com"}?subject=${subject}`,
        "mail",
      );
    },
    [site.email, subject],
  );

  const bookingsHref = useMemo(() => {
    if (process.env.eq1BookingsLinkEnabled === "true") {
      return `https://${site.equiemOneHost}/bookings?app.mode=webng`;
    } else {
      return `/bookings${nsnx}`;
    }
  }, [site.equiemOneHost, nsnx]);

  return (
    <div className={`menu-links ${className}`}>
      <Link href={`/${nsnx}`} className={classNames({ active: /\/$/.test(asPath) })}>

        {t("main.home")}

      </Link>
      <Link
        href={`/news${nsnx}`}
        className={classNames({ active: /\/news(\/.+)?/.test(asPath) })}>

        {t("main.news")}

      </Link>
      <Link
        href={`/events${nsnx}`}
        className={classNames({
          active: /\/(events|event\/.+)/.test(asPath),
        })}>

        {t("main.events")}

      </Link>
      {site.buildingInfo.categories[0] == null ? (
        <Link
          href={`/info${nsnx}`}
          className={classNames({ active: /\/info(\?.+)?$/.test(asPath) })}>

          {site.buildingInfo.title}

        </Link>
      ) : (
        <ParentLink
          title={site.buildingInfo.title}
          openInit={/\/info(\/.+)?/.test(asPath)}
        >
          {site.buildingInfo.categories.map((c, i) => (
            <Link
              key={i}
              href={`/info-category/${c.slug}${nsnx}`}
              className={`small ${classNames({
                active: new RegExp(`/info-category/${c.slug}`).test(asPath),
              })}`}>

              {c.name}

            </Link>
          ))}
          <Link
            href={`/info${nsnx}`}
            className={`small ${classNames({
              active: /\/info(\?.+)?$/.test(asPath),
            })}`}>

            {t("main.all")}

          </Link>
        </ParentLink>
      )}
      {site.featureModules.bookings.enabled &&
      site.featureModules.bookings.enableMenu !== false ? (
        <Link
          href={bookingsHref}
          className={classNames({ active: /\/bookings(\/.+)?/.test(asPath) })}>

          {t("main.bookings")}

        </Link>
      ) : null}
      {site.store.navEnabled ? (
        <Link href={`/store${nsnx}`} id="store-link" className={classNames({ active: /\/store(\/((?!deals).)+)?$/.test(asPath) })}>
          {/* Do not remove store-link class name. It's used in external automated tests */}

          {site.store.title}

        </Link>
      ) : null}
      {site.dealsPage.enabled ? (
        <Link
          href={`/store/deals${nsnx}`}
          className={classNames({ active: /\/store\/deals(\/.+)?/.test(asPath) })}>
          {site.dealsPage.pageName ?? t("main.deals")}
        </Link>
      ) : null}
      {site.featureModules.visitors.enabled &&
      site.featureModules.visitors.enableMenu !== false ? (
        <Link
          href={`/visitor-management${nsnx}`}
          className={classNames({
            active: /\/visitor-management(\/.+)?/.test(asPath),
          })}>

          {t("main.visitorAppointments")}

        </Link>
      ) : null}
      {site.featureModules.requests.enabled &&
      site.featureModules.requests.enableMenu !== false ? (
        <Link
          href={`/request-management${nsnx}`}
          className={classNames({
            active: /\/request-management(\/.+)?/.test(asPath),
          })}>

          {t("main.requestManagement")}

        </Link>
      ) : null}
      {stringNotEmpty(site.email) ? (
        <a
          onClick={mailTo}
          target="_blank"
          href={`mailto:${
            site.email ?? "support@getequiem.com"
          }?subject=${subject}`}
        >
          {t("main.contactUs")}
        </a>
      ) : null}
      {viewer != null ? (
        <Link
          href={`/profile${nsnx}`}
          className={classNames({ active: /\/profile(\/.+)?/.test(asPath) })}>

          {t("main.myAccount")}

        </Link>
      ) : null}
      <style jsx>{`
        .menu-links {
          padding: 32px;
        }
        .menu-links :global(a) {
          color: ${site.primaryContrastColour};
          display: block;
          margin: 0 0 18px;
          width: 100%;
          opacity: 0.7;
          transition: 0.25s ease-in-out;
          font-size: 18px;
        }
        .menu-links :global(a.small) {
          margin: 0 0 12px;
          font-size: 12px;
        }
        .menu-links :global(a:hover),
        .menu-links :global(a.active) {
          opacity: 1;
          text-decoration: none;
        }
        @media (min-width: ${breakpoints.md}px) {
          .menu-links :global(a) {
            margin: 0 0 24px;
            font-size: 24px;
          }
          .menu-links :global(a.small) {
            margin: 0 0 16px;
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};
